import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { getCurrentPhase, getTrackingMode } from '../utils/cycleCalculator'
import allPhasesData from '../data/allPhases.json'
import moonPhasesData from '../data/moonPhases.json'
import CalendarTab from './premium/CalendarTab'
import InsightsTab from './premium/InsightsTab'
import ActivitiesTab from './premium/ActivitiesTab'
import CoachTab from './premium/CoachTab'

const PremiumDashboard = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('activities')
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false)
  const [loading, setLoading] = useState(true)
  const [userProfile, setUserProfile] = useState(null)

  useEffect(() => {
    const checkPremiumAccess = async () => {
      try {
        // Check localStorage for premium access
        const isPremium = localStorage.getItem('isPremium') === 'true'
        
        console.log('Premium Dashboard - isPremium:', isPremium)
        console.log('Premium Dashboard - user:', user?.id)

        if (!isPremium) {
          console.log('No premium access, redirecting to unlock')
          navigate('/unlock')
          return
        }

        setHasPremiumAccess(true)

        // Set up user profile (from user metadata or localStorage)
        if (user) {
          const profile = {
            user_id: user.id,
            full_name: user.user_metadata?.full_name || '',
            tracking_mode: user.user_metadata?.tracking_mode || localStorage.getItem('trackingMode') || 'cycle',
            last_period_date: localStorage.getItem('lastPeriodDate'),
            cycle_length: parseInt(localStorage.getItem('cycleLength')) || 28
          }
          setUserProfile(profile)
        }

      } catch (error) {
        console.error('Error in premium access check:', error)
        navigate('/unlock')
      } finally {
        setLoading(false)
      }
    }

    checkPremiumAccess()
  }, [user, navigate])

  const tabs = [
    { id: 'activities', label: 'Activities', icon: '🎯' },
    { id: 'calendar', label: 'Calendar', icon: '📅' },
    { id: 'insights', label: 'Insights', icon: '📊' },
    { id: 'coach', label: 'Coach', icon: '💬' }
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-6 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your premium dashboard...</p>
        </div>
      </div>
    )
  }

  if (!hasPremiumAccess) {
    return (
      <div className="min-h-screen bg-white px-6 py-8 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-black mb-4">Premium Access Required</h2>
          <p className="text-gray-600 mb-4">You need premium access to view this dashboard.</p>
          <button
            onClick={() => navigate('/unlock')}
            className="gold-bg text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Get Premium Access
          </button>
        </div>
      </div>
    )
  }

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'activities':
        return <ActivitiesTab user={user} userProfile={userProfile} />
      case 'calendar':
        return <CalendarTab user={user} userProfile={userProfile} />
      case 'insights':
        return <InsightsTab user={user} userProfile={userProfile} />
      case 'coach':
        return <CoachTab user={user} userProfile={userProfile} />
      default:
        return <ActivitiesTab user={user} userProfile={userProfile} />
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-md mx-auto">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-black transition-colors"
              >
                ←
              </button>
              <div>
                <h1 className="italiana-font text-xl text-black">
                  Premium Dashboard
                </h1>
                <div className="flex items-center space-x-2">
                  <span className="text-xs bg-gold text-white px-2 py-1 rounded-full">
                    PREMIUM
                  </span>
                  {user && (
                    <span className="text-xs text-gray-500">
                      {user.user_metadata?.full_name || user.email.split('@')[0]}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <button
              onClick={() => navigate(user ? '/profile' : '/settings')}
              className="text-sm text-gray-500 hover:text-black transition-colors"
            >
              Settings
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="max-w-md mx-auto">
          <div className="flex space-x-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-gold text-gold bg-gold bg-opacity-5'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-6 py-6">
        <div className="max-w-md mx-auto">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderActiveTab()}
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PremiumDashboard